import type { SurveySession } from "../../../session/SurveySession";
import type { QuestionGroupChild } from "../../../util/types";
import type { AnyQuestionModule } from "../../contracts";
import type { AttemptDraftState } from "../types";

export const createDraftState = (
  session: SurveySession,
  scopeKey: string,
  draftId: string,
  value: unknown,
  restoredAttempt?: number,
): AttemptDraftState => ({
  session,
  scopeKey,
  values: { [draftId]: value },
  ...(restoredAttempt === undefined
    ? {}
    : { restoredAttempts: { [draftId]: restoredAttempt } }),
});

export const readDraftValue = (
  state: AttemptDraftState | null | undefined,
  session: SurveySession,
  scopeKey: string,
  draftId: string,
  fallback: unknown,
  restoredAttempt?: number,
) =>
  state?.session === session &&
  state.scopeKey === scopeKey &&
  (restoredAttempt === undefined ||
    state.restoredAttempts?.[draftId] === restoredAttempt) &&
  Object.hasOwn(state.values, draftId)
    ? state.values[draftId]
    : fallback;

export const writeDraftValue = (
  state: AttemptDraftState | null,
  session: SurveySession,
  scopeKey: string,
  draftId: string,
  value: unknown,
  restoredAttempt?: number,
): AttemptDraftState => {
  const current =
    state?.session === session && state.scopeKey === scopeKey ? state : null;
  return {
    ...current,
    session,
    scopeKey,
    values: { ...current?.values, [draftId]: value },
    ...(restoredAttempt === undefined
      ? {}
      : {
          restoredAttempts: {
            ...current?.restoredAttempts,
            [draftId]: restoredAttempt,
          },
        }),
  };
};

export const writeSpecifyDraft = (
  state: AttemptDraftState | null,
  session: SurveySession,
  scopeKey: string,
  draftId: string,
  fieldKey: string,
  value: string | undefined,
): AttemptDraftState => {
  const current =
    state?.session === session && state.scopeKey === scopeKey ? state : null;
  const fields = { ...current?.specifyDrafts?.[draftId] };
  if (value === undefined) delete fields[fieldKey];
  else fields[fieldKey] = value;
  return {
    ...current,
    session,
    scopeKey,
    values: current?.values ?? {},
    specifyDrafts: { ...current?.specifyDrafts, [draftId]: fields },
  };
};

export const rejectedResponseVersion = (
  raw: {
    error_message?: string;
    previous_response?: unknown;
    previous_answer_json?: unknown;
  },
  attemptId: number | undefined,
) =>
  raw.error_message &&
  (raw.previous_answer_json !== undefined ||
    raw.previous_response !== undefined)
    ? attemptId
    : undefined;

export const groupDraftValue = (
  state: AttemptDraftState | null | undefined,
  session: SurveySession,
  scopeKey: string,
  child: QuestionGroupChild,
  module: AnyQuestionModule,
  definition: unknown,
) =>
  readDraftValue(
    state,
    session,
    scopeKey,
    child.id,
    module.core.initialize(definition),
    rejectedResponseVersion(child.question, session.getSnapshot().attempt?.id),
  );
